/* Inquiry relay only. No tracking, email, payment or checkout operations.
 * Native contact submission remains authoritative fallback.
 * Staged customer data lives in sessionStorage for <= 1 hour, not localStorage.
 */
(() => {
  'use strict';
  if (window.artisanFreightRelayLoaded) return;
  window.artisanFreightRelayLoaded = true;
  const KEY = 'artisan_freight_relay_pending_v1', TTL = 3600000;
  const ENDPOINT = '/apps/artisan-freight-relay/exec';
  function clear() { try { sessionStorage.removeItem(KEY); } catch (_) {} }
  function pending() {
    try {
      const raw = sessionStorage.getItem(KEY);
      if (!raw || raw.length > 60000) { clear(); return null; }
      const p = JSON.parse(raw);
      const when = Date.parse(p.submitted_at);
      if (!Number.isFinite(when) || Date.now() - when > TTL || when > Date.now() + 60000) { clear(); return null; }
      return p;
    } catch (_) { clear(); return null; }
  }
  function bind() {
    // Document bubbling runs after the existing form-level attribution listener.
    // Do not preventDefault, resubmit, or interfere with native CAPTCHA.
    document.addEventListener('submit', event => {
      const form = event.target;
      // Shopify CAPTCHA may prevent this event and resume the native form later.
      // Staging is not acceptance: only Shopify's success marker can send.
      if (!form || form.id !== 'FreightReviewForm') return;
      clear(); // A later invalid attempt must never reuse an older customer's packet.
      try {
        const data = form.querySelector('[data-artisan-relay-cart]');
        if (!data) return;
        const cart = JSON.parse(data.textContent);
        const quoteNode = form.querySelector('[data-artisan-quote]');
        if (quoteNode) {
          const quote = JSON.parse(quoteNode.textContent);
          if (quote.items.length !== cart.items.length) throw Error('QUOTE_LINES_MISMATCH');
          for (const item of cart.items) {
            const line = quote.items.find(line => line.key === item.key);
            if (!line || !Number.isSafeInteger(line.final_cents) || line.final_cents < 0 || line.final_cents > item.original_line_price) throw Error('QUOTE_PRICE_MISMATCH');
            item.line_price = line.final_cents;
          }
          if (cart.items.reduce((sum,item)=>sum+item.line_price,0) !== quote.total_cents) throw Error('QUOTE_TOTAL_MISMATCH');
          cart.total_price = quote.total_cents;
        }
        const fields = {};
        for (const [key, value] of new FormData(form)) {
          if (/^contact\[/.test(key) && typeof value === 'string') fields[key] = value;
        }
        const id = fields['contact[Inquiry reference]'];
        if (!id) return; // Never fabricate an attribution identity.
        const items = cart.items.map(item => {
          const properties = item.properties || {};
          const configured = /^\d+$/.test(String(properties.Pieces || ''));
          const boring = !configured && /hinge|boring/i.test(item.product_title || item.title || '');
          if (!configured && !boring) throw Error('UNSUPPORTED_ITEM');
          return {
            title:item.product_title || item.title, variant_id:String(item.variant_id),
            key:item.key, kind:configured ? 'configured' : 'boring',
            pieces:configured ? Number(properties.Pieces) : item.quantity,
            // Shopify cart JSON line_price includes allocated cart-wide discounts;
            // final_line_price alone excludes the current Fall Sale allocation.
            regular_cents:item.original_line_price, final_cents:item.line_price,
            properties
          };
        });
        const p = {version:2,inquiry_id:id,submitted_at:new Date().toISOString(),
          currency:cart.currency,total_cents:cart.total_price,items,fields};
        const body = JSON.stringify(p);
        if (body.length > 60000) return;
        sessionStorage.setItem(KEY, body);
      } catch (_) { clear(); } // Native inquiry delivery is never blocked.
    });
  }
  async function accepted() {
    const marker = document.querySelector('[data-artisan-inquiry-accepted="true"]');
    if (!marker || marker.dataset.relayAttempted) return;
    marker.dataset.relayAttempted = 'true';
    const p = pending();
    if (!p) return;
    const body = JSON.stringify(p);
    // Consume before requesting. Ambiguous responses must not cause automated replay.
    clear();
    try {
      const bytes = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(body));
      const hash = Array.from(new Uint8Array(bytes), b => b.toString(16).padStart(2, '0')).join('');
      const response = await fetch(ENDPOINT + '?body_sha256=' + hash, {
        method:'POST',headers:{'Content-Type':'text/plain'},body,
        credentials:'same-origin',cache:'no-store',signal:AbortSignal.timeout(25000)
      });
      const result = await response.json();
      marker.dataset.relayResult = response.ok && result.status === 'ACCEPTED' ? 'accepted' : 'manual-review';
    } catch (_) { marker.dataset.relayResult = 'manual-review'; }
    // Contact-form success message stays unchanged. Staff retains inbox fallback.
  }
  function init() { pending(); bind(); accepted(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
